import clsx from 'clsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import kebabCase from 'lodash/kebabCase';
import React, { useLayoutEffect, useState } from 'react';

import { Poll } from '@utils/backend-api/polls';

import styles from './styles.module.scss';

const SHAREABLE_SCREENSHOT_ID = 'shareable-screenshot';
enum Theme {
  Dark = 'dark',
  Light = 'light',
}

type RenderScreenshotContent = false | Theme;

export const useForumPostScreenshot = (title: string) => {
  const [renderScreenshotContent, setRenderScreenshotContent] =
    useState<RenderScreenshotContent>(false);

  // Clicking download renders a hidden ForumPostScreenshot component
  // and downloads its contents as a screenshot with html2canvas
  const onDownloadScreenshot = (darkMode) => {
    setRenderScreenshotContent(darkMode ? Theme.Dark : Theme.Light);
  };

  const downloadScreenshot = (title: string, onComplete: () => void) => {
    html2canvas(document.getElementById(SHAREABLE_SCREENSHOT_ID), {
      onclone: function cloneDocument(clonedDoc) {
        clonedDoc.getElementById(SHAREABLE_SCREENSHOT_ID).style.display = 'block';
      },
      scale: 2,
    }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, kebabCase(title.substring(0, 20)));
      });
      onComplete();
    });
  };

  useLayoutEffect(() => {
    if (renderScreenshotContent) {
      downloadScreenshot(title, () => setRenderScreenshotContent(false));
    }
  }, [renderScreenshotContent, title]);

  return { onDownloadScreenshot, renderScreenshotContent };
};

const getContainerWidthClass = (length: number) => {
  if (length > 1500) {
    return styles.xxl;
  } else if (length > 1250) {
    return styles.xl;
  } else if (length > 1000) {
    return styles.lg;
  } else if (length > 750) {
    return styles.md;
  }
  return '';
};

function ScreenshotPoll({
  poll,
  showQuestion,
  darkMode,
}: {
  poll: Poll;
  showQuestion: boolean;
  darkMode: boolean;
}) {
  return (
    <div className="mt-24" data-test="forum-post-screenshot-poll">
      {showQuestion && (
        <div className="mb-12 font-suisse-neue text-[18px] font-medium">{poll.question}</div>
      )}
      <div className="flex flex-col gap-12">
        {poll.poll_options.map((option) => (
          <div
            key={option.id}
            className={clsx(
              'relative flex flex-col gap-4 overflow-hidden rounded-4 border border-solid px-16 py-12',
              darkMode ? 'border-grayscale-gray' : 'border-grayscale-beige'
            )}
          >
            <div className="relative z-10 flex justify-between">
              <div className="font-suisse-screen text-body-md">{option.text}</div>
              <div className="font-suisse-screen text-body-md">{option.percentage}%</div>
            </div>
            <div
              className="absolute left-0 top-0 h-full bg-primary-pink"
              style={{ width: `${option.percentage}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ForumPostScreenshot({
  title,
  body,
  renderScreenshotContent,
  length,
  poll,
  isPollOnly = false,
}: {
  title: string;
  body: string;
  renderScreenshotContent: RenderScreenshotContent;
  length: number;
  poll?: Poll;
  isPollOnly?: boolean;
}) {
  const darkMode = renderScreenshotContent === Theme.Dark;

  return (
    <div
      id={SHAREABLE_SCREENSHOT_ID}
      className={clsx(styles.forumPostScreenshotContainer, getContainerWidthClass(length), {
        [styles.formPostScreenshotContainerDark]: darkMode,
      })}
    >
      <h1
        className={`my-0 font-suisse-works text-headline-sm sm:text-headline-lg ${
          darkMode ? 'text-white' : 'text-black'
        }`}
      >
        The Information <span className="text-primary-magenta">Forum</span>
      </h1>
      <div className="font-suisse-neue text-subheadline-md">{title}</div>
      {!isPollOnly && <div className="mt-24 text-body-lg">{body}</div>}
      {poll && <ScreenshotPoll poll={poll} showQuestion={!isPollOnly} darkMode={darkMode} />}
    </div>
  );
}
